import { useState } from "react";
import { Download, FileText, FolderOpen, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, EmptyState } from "@/components/Common";
import { MemberGate } from "@/components/RequestDialogs";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr, isMember, useFetch } from "@/lib/api";

export const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export async function downloadResource(r) {
  const res = await api.get(`/resources/${r.id}/download`, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = r.filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function Resources() {
  const { user } = useAuth();
  const { data } = useFetch("/resources");
  const [busy, setBusy] = useState(null);
  const member = isMember(user);
  const groups = (data || []).reduce((acc, r) => ({ ...acc, [r.category]: [...(acc[r.category] || []), r] }), {});

  const dl = async (r) => {
    setBusy(r.id);
    try { await downloadResource(r); } catch (e) { toast.error(formatErr(e)); }
    setBusy(null);
  };

  return (
    <div>
      <PageHeader eyebrow="Included with membership" title="Business files" desc="Templates, checklists and documents to help you manage your virtual team." />
      {!member && <div className="mb-8"><MemberGate /></div>}
      {data?.length === 0 && <EmptyState testId="resources-empty" icon={FolderOpen} title="No files yet" desc="Our team is adding templates soon." />}
      <div className="space-y-10">
        {Object.entries(groups).map(([cat, items]) => (
          <section key={cat}>
            <p className="mb-3 text-xs font-mono uppercase tracking-[0.18em] text-stone-500">{cat}</p>
            <div className="grid gap-4 md:grid-cols-2">
              {items.map((r) => (
                <div key={r.id} data-testid={`resource-card-${r.id}`} className="lift flex items-start gap-4 rounded-md border border-stone-200 bg-white p-6">
                  <div className="grid h-11 w-11 shrink-0 place-items-center rounded-md bg-stone-100"><FileText className="h-5 w-5 text-stone-600" strokeWidth={1.5} /></div>
                  <div className="min-w-0 flex-1">
                    <p className="font-display font-bold text-stone-900">{r.title}</p>
                    <p className="mt-1 text-sm text-stone-600">{r.description}</p>
                    <p className="mt-2 text-[11px] font-mono uppercase text-stone-400">{r.filename.split(".").pop()} · {fmtSize(r.size)}</p>
                  </div>
                  <button data-testid={`resource-download-${r.id}`} disabled={!member || busy === r.id} onClick={() => dl(r)} className="rounded-md border border-stone-300 p-2.5 text-stone-700 transition-colors hover:bg-slate-900 hover:text-white disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-stone-700">
                    {busy === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  </button>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
